import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Star, MessageSquare, RefreshCw, Loader2, AlertTriangle,
  Eye, TrendingUp, ThumbsDown, Calendar, Globe, EyeOff, Check
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import AdminSidebar from '../../components/admin/AdminSidebar';

const API_URL = window.location.origin;

export default function AdminFeedbackPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState([]);
  const [stats, setStats] = useState({ averageRating: 0, totalFeedback: 0, lowRatingCount: 0, unreadLowRating: 0 });
  const [alerts, setAlerts] = useState([]);
  
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [ratingFilter, setRatingFilter] = useState('');
  
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  
  const token = localStorage.getItem('admin_token') || localStorage.getItem('vipgez_admin_token') || localStorage.getItem('adminToken');

  useEffect(() => {
    if (!token) {
      navigate('/login-goturkeytransfer');
    }
  }, [token, navigate]);

  const fetchFeedback = useCallback(async () => {
    try {
      let url = `${API_URL}/api/admin/feedback?page=${page}&limit=20`;
      if (ratingFilter && ratingFilter !== 'all') url += `&rating=${ratingFilter}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.status === 401) {
        navigate('/login-goturkeytransfer');
        return;
      }
      
      const data = await response.json();
      if (data.success) {
        setFeedback(data.feedback);
        setTotalPages(data.totalPages);
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching feedback:', error);
    }
  }, [token, navigate, page, ratingFilter]);

  const fetchAlerts = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/alerts?unread_only=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (data.success) {
        setAlerts(data.alerts);
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchFeedback(), fetchAlerts()]);
      setLoading(false);
    };
    loadData();
  }, [fetchFeedback, fetchAlerts]);

  const handleMarkViewed = async (bookingRef) => {
    try {
      await fetch(`${API_URL}/api/admin/feedback/${bookingRef}/viewed`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Marked as viewed');
      fetchFeedback();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleToggleDisplay = async (bookingRef, e) => {
    e?.stopPropagation();
    try {
      const response = await fetch(`${API_URL}/api/admin/feedback/${bookingRef}/toggle-display`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        fetchFeedback();
        // Update selected feedback if open
        if (selectedFeedback?.booking_ref === bookingRef) {
          setSelectedFeedback(prev => ({...prev, approved_for_display: data.approved_for_display}));
        }
      }
    } catch (error) {
      toast.error('Failed to toggle display');
    }
  };

  const handleHideFeedback = async (bookingRef, e) => {
    e?.stopPropagation();
    try {
      const response = await fetch(`${API_URL}/api/admin/feedback/${bookingRef}/hide`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success('Review hidden from homepage');
        fetchFeedback();
        setSelectedFeedback(null);
      }
    } catch (error) {
      toast.error('Failed to hide review');
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-muted-foreground'
            }`}
          />
        ))}
      </div>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('tr-TR');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex admin-theme">
        <AdminSidebar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex admin-theme">
      <AdminSidebar />
      <main className="flex-1 p-6 lg:p-8 overflow-auto">
        <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Customer Feedback</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Monitor customer satisfaction and feedback
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => { fetchFeedback(); fetchAlerts(); }}
          className="gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="bg-secondary border-border">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-yellow-500/10 rounded-lg">
                <Star className="h-6 w-6 text-yellow-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stats.averageRating.toFixed(1)}</p>
                <p className="text-sm text-muted-foreground">Average Rating</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-secondary border-border">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-500/10 rounded-lg">
                <MessageSquare className="h-6 w-6 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stats.totalFeedback}</p>
                <p className="text-sm text-muted-foreground">Total Feedback</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-secondary border-border">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-500/10 rounded-lg">
                <ThumbsDown className="h-6 w-6 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stats.lowRatingCount}</p>
                <p className="text-sm text-muted-foreground">Low Ratings (≤3)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-secondary border-border">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-500/10 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stats.unreadLowRating}</p>
                <p className="text-sm text-muted-foreground">Needs Attention</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Low Rating Alerts */}
      {alerts.length > 0 && (
        <Card className="border-red-500/30 bg-red-500/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-red-400">
              <AlertTriangle className="h-4 w-4" />
              Low Rating Alerts ({alerts.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {alerts.slice(0, 5).map((alert, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm bg-muted/30 p-2 rounded">
                  <div className="flex items-center gap-3">
                    {renderStars(alert.rating)}
                    <span className="font-medium text-foreground">{alert.booking_ref}</span>
                    <span className="text-muted-foreground">- {alert.customer_name}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(alert.created_at)}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter */}
      <div className="flex gap-4 items-center">
        <Select value={ratingFilter} onValueChange={(v) => { setRatingFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Ratings" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Ratings</SelectItem>
            <SelectItem value="5">⭐⭐⭐⭐⭐ (5)</SelectItem>
            <SelectItem value="4">⭐⭐⭐⭐ (4)</SelectItem>
            <SelectItem value="3">⭐⭐⭐ (3)</SelectItem>
            <SelectItem value="2">⭐⭐ (2)</SelectItem>
            <SelectItem value="1">⭐ (1)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Feedback List */}
      <div className="grid gap-4">
        {feedback.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              No feedback found
            </CardContent>
          </Card>
        ) : (
          feedback.map((item, idx) => (
            <Card 
              key={idx} 
              className={`cursor-pointer transition-all hover:shadow-md ${
                item.is_low_rating && !item.admin_viewed ? 'border-red-300 bg-red-50' : ''
              } ${item.hidden ? 'opacity-50' : ''}`}
              onClick={() => setSelectedFeedback(item)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-2 flex-wrap">
                      {renderStars(item.rating)}
                      <span className="font-medium text-orange-600">{item.booking_ref}</span>
                      {item.is_low_rating && !item.admin_viewed && (
                        <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded">
                          Needs Review
                        </span>
                      )}
                      {/* Display status badge */}
                      {item.rating >= 4 && !item.hidden && (
                        <span className={`px-2 py-0.5 text-xs rounded flex items-center gap-1 ${
                          item.approved_for_display 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          <Globe className="h-3 w-3" />
                          {item.approved_for_display ? 'Anasayfada' : 'Otomatik Onaylı'}
                        </span>
                      )}
                      {item.hidden && (
                        <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded flex items-center gap-1">
                          <EyeOff className="h-3 w-3" />
                          Gizli
                        </span>
                      )}
                    </div>
                    
                    <div className="text-sm text-muted-foreground mb-2">
                      <span className="font-medium text-foreground">{item.customer_name}</span>
                      <span className="text-muted-foreground mx-2">•</span>
                      <span>{item.route}</span>
                    </div>
                    
                    {item.comment && (
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                        &quot;{item.comment}&quot;
                      </p>
                    )}
                  </div>
                  
                  <div className="text-right text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {formatDate(item.submitted_at)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Feedback Detail Dialog */}
      <Dialog open={!!selectedFeedback} onOpenChange={() => setSelectedFeedback(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Feedback Details</DialogTitle>
          </DialogHeader>
          
          {selectedFeedback && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {renderStars(selectedFeedback.rating)}
                  <span className="font-bold text-lg">
                    {selectedFeedback.rating}/5
                  </span>
                </div>
                <span className="text-orange-600 font-medium">
                  {selectedFeedback.booking_ref}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Customer:</span>
                  <p className="font-medium">{selectedFeedback.customer_name}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Email:</span>
                  <p className="font-medium">{selectedFeedback.customer_email}</p>
                </div>
                <div className="col-span-2">
                  <span className="text-muted-foreground">Route:</span>
                  <p className="font-medium">{selectedFeedback.route}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Trip Date:</span>
                  <p className="font-medium">{selectedFeedback.trip_date}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Submitted:</span>
                  <p className="font-medium">{formatDate(selectedFeedback.submitted_at)}</p>
                </div>
              </div>
              
              {selectedFeedback.comment && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <span className="text-xs text-muted-foreground uppercase">Comment</span>
                  <p className="mt-1 text-foreground">{selectedFeedback.comment}</p>
                </div>
              )}
              
              {/* Homepage Display Controls - Only for 4-5 star reviews with comments */}
              {selectedFeedback.rating >= 4 && selectedFeedback.comment && !selectedFeedback.hidden && (
                <div className="p-4 bg-blue-50 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-medium text-blue-900">Anasayfa Görünürlüğü</span>
                      <p className="text-xs text-blue-700">
                        {selectedFeedback.approved_for_display 
                          ? '✅ Bu yorum anasayfada gösteriliyor' 
                          : 'ℹ️ 4-5 yıldızlı yorumlar otomatik onaylı'}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant={selectedFeedback.approved_for_display ? "outline" : "default"}
                      onClick={(e) => handleToggleDisplay(selectedFeedback.booking_ref, e)}
                      className={selectedFeedback.approved_for_display ? "" : "bg-green-600 hover:bg-green-700"}
                    >
                      {selectedFeedback.approved_for_display ? (
                        <>
                          <EyeOff className="h-4 w-4 mr-1" />
                          Kaldır
                        </>
                      ) : (
                        <>
                          <Globe className="h-4 w-4 mr-1" />
                          Öne Çıkar
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Hide Review Button */}
              {!selectedFeedback.hidden && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => handleHideFeedback(selectedFeedback.booking_ref, e)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <EyeOff className="h-4 w-4 mr-1" />
                  Yorumu Kalıcı Gizle
                </Button>
              )}
              
              {selectedFeedback.is_low_rating && !selectedFeedback.admin_viewed && (
                <Button
                  onClick={() => {
                    handleMarkViewed(selectedFeedback.booking_ref);
                    setSelectedFeedback(null);
                  }}
                  className="w-full bg-orange-500 hover:bg-orange-600"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Mark as Reviewed
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
        </div>
      </main>
    </div>
  );
}
